import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { AVideo } from '../../atoms/AVideo/index';
import { type BrightcovePlayerRef } from '../../atoms/AVideo/AVideo.types';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { MButtonOverlay } from '../MButtonOverlay/index';
import { isBrightcove } from '../../utils/videoUtils';
import * as Styled from './MVideoBackground.styles';
import type { MVideoBackgroundProps } from './MVideoBackground.types';
import {
  MVideoBackgroundButtonPosition,
  MVideoBackgroundErrorMessage,
  MVideoBackgroundAriaLabel,
} from './MVideoBackground.types';
import type { MButtonOverlayProps } from '../MButtonOverlay/MButtonOverlay.types';

const COMPONENT_NAME = 'm-web-ui-kit-MVideoBackground';
const TEST_ID = 'web-ui-kit-MVideoBackground';

const DEFAULT_BUTTON_OVERLAY: MButtonOverlayProps = {
  iconOnly: true,
  imageOverlay: true,
  aIcon: { icon: 'icon-pause', size: AIconSize.L },
};

interface ErrorWithMessage {
  message: string;
}

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
  typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string';

const getErrorMessage = (error: unknown): string =>
  isErrorWithMessage(error) ? error.message : MVideoBackgroundErrorMessage.Default;

export const MVideoBackground: FC<MVideoBackgroundProps> = ({
  buttonPosition = MVideoBackgroundButtonPosition.Bottom,
  fillContainer,
  autoPlay = false,
  aVideo,
  mButtonOverlay,
  className,
  ...nativeProps
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay && aVideo.isPlaying !== false);
  const playHandlerRef = useRef<(() => void) | null>(null);
  const pauseHandlerRef = useRef<(() => void) | null>(null);
  const playerRef = useRef<BrightcovePlayerRef | null>(null);
  // Tracks whether the user explicitly paused via the play/pause toggle button.
  // handleTogglePlay writes `prev` (the pre-toggle playing state) into this ref
  // unconditionally: true when the user pauses, false when the user resumes.
  // Guards against parent-driven isPlaying=true resuming a video the user
  // intentionally paused.
  const userPausedRef = useRef(false);
  const prevIsPlayingPropRef = useRef(aVideo.isPlaying);

  useEffect(() => {
    if (aVideo.isPlaying === prevIsPlayingPropRef.current) return;
    prevIsPlayingPropRef.current = aVideo.isPlaying;

    if (aVideo.isPlaying === false) {
      setIsPlaying(false);
    } else if (aVideo.isPlaying === true && autoPlay && !userPausedRef.current) {
      setIsPlaying(true);
    }
  }, [aVideo.isPlaying, autoPlay]);

  useEffect(() => {
    if (autoPlay && aVideo.isPlaying !== false && !userPausedRef.current) {
      setIsPlaying(true);
    } else if (!autoPlay) {
      setIsPlaying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only reacts to autoPlay changes
  }, [autoPlay]);

  const handlePlayerError = useCallback((error?: unknown) => {
    setErrorMessage(getErrorMessage(error));
  }, []);

  const hasPlayedRef = useRef(false);

  const handlePlayerReady = useCallback((player: BrightcovePlayerRef) => {
    playerRef.current = player;

    if (typeof player.on === 'function') {
      if (typeof player.off === 'function') {
        if (playHandlerRef.current) player.off('play', playHandlerRef.current);
        if (pauseHandlerRef.current) player.off('pause', pauseHandlerRef.current);
      }

      const handlePlay = () => {
        hasPlayedRef.current = true;
        setIsPlaying(true);
      };
      const handlePause = () => {
        if (hasPlayedRef.current) {
          setIsPlaying(false);
        }
      };

      playHandlerRef.current = handlePlay;
      pauseHandlerRef.current = handlePause;

      player.on('play', handlePlay);
      player.on('pause', handlePause);
    }
  }, []);

  const handleTogglePlay = useCallback(() => {
    setIsPlaying(prev => {
      userPausedRef.current = prev;
      return !prev;
    });
  }, []);

  const buttonProps = mButtonOverlay ?? DEFAULT_BUTTON_OVERLAY;
  const effectiveIsPlaying = aVideo.isPlaying === false ? false : isPlaying;

  return (
    <Styled.Wrapper
      {...nativeProps}
      $aspectRatio={aVideo.aspectRatio}
      $fillContainer={fillContainer}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={className}
    >
      <Styled.VideoContainer>
        <AVideo
          {...aVideo}
          autoPlay={effectiveIsPlaying}
          muted={true}
          loop={true}
          controls={false}
          isPlaying={effectiveIsPlaying}
          {...(isBrightcove(aVideo) && {
            onPlayerReady: handlePlayerReady,
            onPlayerError: handlePlayerError,
          })}
        />
      </Styled.VideoContainer>

      {errorMessage && (
        <Styled.ErrorOverlay role="alert" aria-live="assertive" aria-atomic="true">
          <Styled.ErrorText>{errorMessage}</Styled.ErrorText>
        </Styled.ErrorOverlay>
      )}

      {!errorMessage && (
        <Styled.OverlayContainer $buttonPosition={buttonPosition}>
          <MButtonOverlay
            {...buttonProps}
            imageOverlay={true}
            aria-label={effectiveIsPlaying ? MVideoBackgroundAriaLabel.Pause : MVideoBackgroundAriaLabel.Play}
            aIcon={{
              ...buttonProps.aIcon,
              icon: effectiveIsPlaying ? 'icon-pause' : 'icon-play',
              size: AIconSize.L,
            }}
            onClick={handleTogglePlay}
          />
        </Styled.OverlayContainer>
      )}
    </Styled.Wrapper>
  );
};

export * from './MVideoBackground.types';
